// src/context/SocketContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../modules/Base/Auth/contexts/AuthContext';
import { refreshToken as refreshTokenService } from '../services/tokenService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const SocketContext = createContext();

export function SocketProvider({ children }) {
  const socketRef = useRef(null);

  // ДЕСТРУКТУРИЗАЦИЯ ИЗ useAuth
  const {
    isAuthenticated,
    accessToken,
    logout
  } = useAuth();

  const [ready, setReady] = useState(false);

  useEffect(() => {
    // 1) Если вышли — полностью демонтируем сокет
    if (!isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setReady(false);
        console.log('Socket torn down on logout');
      }
      return;
    }

    // 2) Если уже есть живое соединение — не трогаем
    if (socketRef.current) {
      return;
    }

    // 3) Создаём новый сокет
    const sock = io(API_BASE_URL, {
      path: '/ws',
      transports: ['websocket'],
      auth: { token: accessToken },  // <-- здесь доступен accessToken
      autoConnect: false,
      withCredentials: true,
    });

    // 4) При ошибке handshake пробуем один раз рефрешить
    sock.on('connect_error', async err => {
      console.warn('⚠️ Socket connect_error:', err.message);
      if (/invalid|expired/i.test(err.message) && !sock._didRefresh) {
        sock._didRefresh = true;
        try {
          const newToken = await refreshTokenService();  // общий сервис
          if (!newToken) throw new Error('NoNewToken');

          // обновляем токен «на лету»
          sock.auth = { token: newToken };
          sock.io.opts.auth = { token: newToken };
          console.log('🔄 Token refreshed, reconnecting socket…');
          sock.connect();
        } catch (refreshErr) {
          console.error('❌ Socket token refresh failed:', refreshErr);
          //logout(); 
        }
      } else {
        //logout();
      }
    });

    // 5) Логируем успешное подключение
    sock.on('connect', () => {
      console.log('🟢 Socket connected:', sock.id);
      setReady(true);
    });

    // 6) Логируем отключение
    sock.on('disconnect', reason => {
      console.log('🔴 Socket disconnected:', reason);
      setReady(false);

      if (reason === 'transport close' || reason === 'io server disconnect') {
        console.log('↩️ Попытка переподключения через 2с…');
        setTimeout(() => {
          if (sock && !sock.connected) {
            sock.connect();
          }
        }, 2000);
      }
    });


    // 7) Запускаем
    sock.connect();
    socketRef.current = sock;

    // 8) Cleanup при демонтировании провайдера или logout
    return () => {
      sock.disconnect();
      socketRef.current = null;
      setReady(false);
      console.log('Socket cleanup');
    };
  }, [
    // <-- единственная зависимость — только переход между анонимным и залогиненным
    isAuthenticated,
    // accessToken и logout использованы в эффекте, но нам не нужно их включать,
    // чтобы не пересоздавать сокет при каждом обновлении токена.
    // React не будет ворчать, если мы их закомментируем для eslint:
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  return (
    <SocketContext.Provider value={socketRef.current}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const socket = useContext(SocketContext);
  if (socket === undefined) {
    throw new Error('useSocket must be inside SocketProvider');
  }
  return socket;
}
